import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { fmtDate, type AdminUser, type PageMeta } from '../lib/types'
import { StatusBadge } from '../components/StatusBadge'

const REJECT_PRESETS = [
  'Документ не читается',
  'Данные не совпадают',
  'Документ просрочен',
  'Отсутствует необходимая сторона документа',
  'Требуется другой документ',
]

type IdentityDocs = {
  passport_main_url?: string | null
  passport_reg_url?: string | null
  expires_in?: number
}

export function VerificationPage() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') || 'pending'
  const qc = useQueryClient()
  const [selected, setSelected] = useState<AdminUser | null>(null)
  const [reason, setReason] = useState('')
  const [docs, setDocs] = useState<IdentityDocs | null>(null)
  const [docsError, setDocsError] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['identity', status],
    queryFn: () => api<{ items: AdminUser[]; meta: PageMeta }>(`/admin/identity?status=${status}&page_size=50`),
  })

  useEffect(() => {
    if (!selected) {
      setDocs(null)
      setDocsError('')
      return
    }
    let cancelled = false
    setDocs(null)
    setDocsError('')
    api<IdentityDocs>(`/admin/users/${selected.id}/identity/documents`)
      .then((d) => { if (!cancelled) setDocs(d) })
      .catch((e) => { if (!cancelled) setDocsError(e instanceof Error ? e.message : 'Ошибка документов') })
    return () => { cancelled = true }
  }, [selected?.id])

  const approve = useMutation({
    mutationFn: (id: string) => api(`/admin/users/${id}/identity/approve`, { method: 'POST', body: '{}' }),
    onSuccess: () => { setSelected(null); qc.invalidateQueries({ queryKey: ['identity'] }) },
  })
  const reject = useMutation({
    mutationFn: (id: string) => api(`/admin/users/${id}/identity/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => { setSelected(null); setReason(''); qc.invalidateQueries({ queryKey: ['identity'] }) },
  })

  return (
    <div>
      <h1 className="page-title">Верификация</h1>
      <p className="page-sub">Очередь документов пользователей</p>
      <div className="toolbar">
        {['pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            className={`btn ${status === s ? 'btn-primary' : ''}`}
            onClick={() => setParams({ status: s })}
          >
            {s === 'pending' ? 'Ожидают' : s === 'approved' ? 'Одобрены' : 'Отклонены'}
          </button>
        ))}
      </div>
      <div className="two-col">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Дата</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={4}>Загрузка…</td></tr>}
              {data?.items.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => {
                    api<AdminUser>(`/admin/users/${u.id}`).then(setSelected).catch(() => setSelected(u))
                  }}
                  style={{ background: selected?.id === u.id ? '#eff6ff' : undefined }}
                >
                  <td>{u.name}<div className="muted">{u.email}</div></td>
                  <td>{u.account_type === 'customer' ? 'Заказчик' : 'Исполнитель'}</td>
                  <td><StatusBadge value={u.identity_status} /></td>
                  <td>{fmtDate(u.created_at)}</td>
                </tr>
              ))}
              {!isLoading && data?.items.length === 0 && <tr><td colSpan={4}>Пусто</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="card stack">
          {!selected && <p className="muted">Выберите заявку слева</p>}
          {selected && (
            <>
              <div>
                <strong>{selected.name}</strong>
                <div className="muted">{selected.email}</div>
                <Link to={`/users/${selected.id}`}>Открыть карточку →</Link>
              </div>
              {docsError && <div className="error">{docsError}</div>}
              {!docs && !docsError && <div className="muted">Загрузка документов…</div>}
              <div className="doc-grid">
                <div>
                  <div className="muted">Первая страница</div>
                  {docs?.passport_main_url
                    ? <img src={docs.passport_main_url} alt="main" />
                    : <div className="muted">Нет</div>}
                </div>
                <div>
                  <div className="muted">Регистрация</div>
                  {docs?.passport_reg_url
                    ? <img src={docs.passport_reg_url} alt="reg" />
                    : <div className="muted">Нет</div>}
                </div>
              </div>
              {status === 'pending' && (
                <>
                  <button className="btn btn-success" onClick={() => approve.mutate(selected.id)}>Одобрить</button>
                  <div className="row">
                    {REJECT_PRESETS.map((p) => (
                      <button key={p} className="btn" type="button" onClick={() => setReason(p)}>{p}</button>
                    ))}
                  </div>
                  <textarea className="input" style={{ minHeight: 80, width: '100%' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Причина отказа" />
                  <button className="btn btn-danger" disabled={!reason.trim()} onClick={() => reject.mutate(selected.id)}>Отклонить</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
